import { useState } from 'react';
import {
  useFiscalYearLocks,
  usePrecheck,
  useLockFiscalYear,
  useUnlockFiscalYear,
  useUpdateDeclarationStatus,
} from '../hooks/useFiscalYearLocks';

const declStatusLabels: Record<string, string> = {
  not_filed: '未申告',
  csv_exported: 'CSV出力済',
  filed: '申告完了',
};

export default function YearEndPage() {
  const currentYear = new Date().getFullYear();
  const [fiscalYear, setFiscalYear] = useState(currentYear - 1);
  const { data: locks } = useFiscalYearLocks();
  const { data: precheck, isFetching: precheckLoading } = usePrecheck(fiscalYear);
  const lockMut = useLockFiscalYear();
  const unlockMut = useUnlockFiscalYear();
  const updateStatusMut = useUpdateDeclarationStatus();

  const lockedThis = locks?.find((l) => l.fiscalYear === fiscalYear);

  const onLock = async () => {
    if (!precheck?.canLock) {
      alert('事前チェックNG。表示中の問題を解消してください');
      return;
    }
    const reason = prompt(`${fiscalYear}年度を締めます。締め理由を入力してください（任意）：`) ?? '';
    try {
      await lockMut.mutateAsync({ fiscalYear, reason: reason.trim() || undefined });
      alert(`${fiscalYear}年度を締め処理しました`);
    } catch (err) {
      alert(err instanceof Error ? err.message : '締め処理失敗');
    }
  };

  const onUnlock = async (fy: number) => {
    if (!confirm(`${fy}年度のロックを解除します。本当によろしいですか？\n（緊急訂正用・申告完了済の場合は不可）`)) return;
    try {
      await unlockMut.mutateAsync(fy);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'ロック解除失敗');
    }
  };

  const onMarkFiled = async (fy: number) => {
    const filedAt = prompt('申告日を入力（YYYY-MM-DD・空白で本日）：') ?? '';
    try {
      await updateStatusMut.mutateAsync({
        fiscalYear: fy,
        declarationStatus: 'filed',
        filedAt: filedAt.trim() || undefined,
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : '更新失敗');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>年度締め・申告状況</h2>
      </div>

      <div className="card">
        <h3>事前チェック・締め処理</h3>
        <div className="toolbar">
          <label>
            事業年度{' '}
            <input
              type="number"
              value={fiscalYear}
              onChange={(e) => setFiscalYear(Number(e.target.value))}
              style={{ width: 100 }}
            />
          </label>
        </div>

        {precheckLoading && <div>チェック中...</div>}

        {precheck && (
          <>
            <div
              className={precheck.canLock ? 'success-banner' : 'error-banner'}
              style={{ marginTop: 16 }}
            >
              {precheck.canLock
                ? `✓ ${fiscalYear}年度は締め処理可能です（仕訳 ${precheck.details.entryCount} 件）`
                : `${precheck.blockers.length} 件の問題があります`}
            </div>

            {precheck.blockers.length > 0 && (
              <ul style={{ marginLeft: 20 }}>
                {precheck.blockers.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}

            {precheck.details.unbalancedEntries.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <strong>借貸不一致仕訳：</strong>
                <table style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>取引日</th>
                      <th>摘要</th>
                      <th className="numeric">借方</th>
                      <th className="numeric">貸方</th>
                    </tr>
                  </thead>
                  <tbody>
                    {precheck.details.unbalancedEntries.map((u) => (
                      <tr key={u.entryId}>
                        <td>{u.entryDate}</td>
                        <td>{u.description}</td>
                        <td className="numeric">{u.debit.toLocaleString()}</td>
                        <td className="numeric">{u.credit.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {precheck.details.accountsWithoutNtaCode.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <strong>国税庁区分コード未設定科目：</strong>
                <ul>
                  {precheck.details.accountsWithoutNtaCode.map((a) => (
                    <li key={a.id}>
                      {a.code} {a.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="toolbar" style={{ marginTop: 16 }}>
              {!lockedThis ? (
                <button
                  className="primary"
                  onClick={onLock}
                  disabled={!precheck.canLock || lockMut.isPending}
                >
                  {fiscalYear}年度を締める
                </button>
              ) : (
                <span className="badge active">締め処理済（{lockedThis.lockedAt}）</span>
              )}
            </div>
          </>
        )}
      </div>

      <div className="page-header" style={{ marginTop: 32 }}>
        <h3 style={{ margin: 0 }}>締め済み年度・申告状況</h3>
      </div>
      <table>
        <thead>
          <tr>
            <th>事業年度</th>
            <th>締め日時</th>
            <th>締め理由</th>
            <th>申告状況</th>
            <th>CSV出力</th>
            <th>申告日</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {locks?.map((l) => (
            <tr key={l.fiscalYear}>
              <td>{l.fiscalYear}</td>
              <td>{l.lockedAt.slice(0, 16).replace('T', ' ')}</td>
              <td>{l.lockedReason ?? <span className="text-muted">—</span>}</td>
              <td>
                <span
                  className={`badge ${l.declarationStatus === 'filed' ? 'active' : l.declarationStatus === 'csv_exported' ? 'draft' : 'voided'}`}
                >
                  {declStatusLabels[l.declarationStatus]}
                </span>
              </td>
              <td>{l.csvExportedAt ? l.csvExportedAt.slice(0, 10) : <span className="text-muted">—</span>}</td>
              <td>{l.filedAt ?? <span className="text-muted">—</span>}</td>
              <td>
                {l.declarationStatus !== 'filed' && (
                  <>
                    <button onClick={() => onMarkFiled(l.fiscalYear)}>申告完了</button>{' '}
                    <button onClick={() => onUnlock(l.fiscalYear)} className="danger">
                      ロック解除
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {(!locks || locks.length === 0) && (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="text-muted">
                締め処理済みの年度はありません
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
